import React from "react";
import { motion } from "framer-motion";
import { HandHeart, Lightbulb, PiggyBank, Handshake } from "@phosphor-icons/react";

export function WhyChooseUs() {
  const pillars = [
    {
      icon: HandHeart,
      title: "Incredible Customer Service",
      desc: "We show up when we say we will, communicate clearly, and treat every property like our own.",
    },
    {
      icon: Lightbulb,
      title: "Common Sense Fixes",
      desc: "Practical, thoughtful solutions that actually solve the problem — no overcomplicating, no upselling.",
    },
    {
      icon: PiggyBank,
      title: "Flexible & Affordable",
      desc: "One-time jobs or ongoing weekly, bi-weekly, or monthly plans that fit your life and budget.",
    },
    {
      icon: Handshake,
      title: "One Call for Everything",
      desc: "Cleaning, handyman work, yard help, car care, and tech setup — all from one trusted team.",
    },
  ];

  return (
    <section id="why-us" className="py-24 md:py-32 bg-sky-50/60" data-testid="why-us-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl">
          <div className="text-xs font-semibold uppercase tracking-widest text-sky-700">
            Why Customers Love Us
          </div>
          <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-display font-bold text-slate-900 tracking-tight">
            Built on trust, not shortcuts.
          </h2>
          <p className="mt-5 text-lg text-slate-600 leading-relaxed">
            We built Heavenly Home And Office to be the reliable neighbor you can call when
            something needs doing — and actually get it done right.
          </p>
        </div>

        <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {pillars.map((p, i) => (
            <motion.div
              key={p.title}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.45, delay: i * 0.06 }}
              className="bg-white rounded-3xl p-8 border border-slate-100 hover:border-sky-200 hover:-translate-y-1 transition-all duration-300"
              data-testid={`pillar-${i}`}
            >
              <div className="w-12 h-12 rounded-2xl bg-sky-500/10 text-sky-600 flex items-center justify-center">
                <p.icon size={26} weight="duotone" />
              </div>
              <h3 className="mt-5 text-lg font-display font-semibold text-slate-900">{p.title}</h3>
              <p className="mt-3 text-[15px] text-slate-600 leading-relaxed">{p.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function ProcessSection() {
  const steps = [
    { n: "01", title: "Tell us what you need", desc: "A quick call, text, or form submission — no pressure, no technical jargon." },
    { n: "02", title: "Get a simple quote", desc: "Fair, upfront pricing. We recommend the right plan for your situation." },
    { n: "03", title: "Pick a time that works", desc: "One-time, weekly, bi-weekly, or monthly — on your schedule." },
    { n: "04", title: "We show up and get it done", desc: "Friendly crew, great communication, and work we're proud of." },
  ];

  return (
    <section id="process" className="py-24 md:py-32 bg-white" data-testid="process-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl">
          <div className="text-xs font-semibold uppercase tracking-widest text-sky-700">
            How Scheduling Works
          </div>
          <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-display font-bold text-slate-900 tracking-tight">
            Flexible scheduling that fits your life.
          </h2>
          <p className="mt-5 text-lg text-slate-600 leading-relaxed">
            Whether it&apos;s a deep clean before a holiday, a recurring office tidy, or a one-off
            handyman fix — just describe your situation and we&apos;ll suggest a plan.
          </p>
        </div>

        <div className="mt-16 grid grid-cols-1 md:grid-cols-4 gap-6 relative">
          <div className="hidden md:block absolute top-8 left-[12%] right-[12%] border-t-2 border-dashed border-sky-200" />
          {steps.map((s, i) => (
            <motion.div
              key={s.n}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.45, delay: i * 0.08 }}
              className="relative bg-white z-10"
              data-testid={`process-step-${i}`}
            >
              <div className="w-16 h-16 rounded-full bg-sky-500 text-white flex items-center justify-center font-display font-bold text-lg shadow-lg shadow-sky-500/30 mx-auto md:mx-0">
                {s.n}
              </div>
              <h3 className="mt-5 text-lg font-display font-semibold text-slate-900 text-center md:text-left">
                {s.title}
              </h3>
              <p className="mt-2 text-[15px] text-slate-600 leading-relaxed text-center md:text-left">
                {s.desc}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function AboutSection() {
  return (
    <section id="about" className="py-24 md:py-32 bg-stone-50" data-testid="about-section">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl">
          <div className="text-xs font-semibold uppercase tracking-widest text-sky-700">
            About Heavenly Home And Office
          </div>
          <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-display font-bold text-slate-900 tracking-tight">
            A single, dependable team for the little things — and the big ones.
          </h2>
          <div className="mt-8 space-y-5 text-lg text-slate-600 leading-relaxed">
            <p>
              We built Heavenly Home And Office to be the one number busy people can call when
              they need help around the home or office. Cleaning, handyman work, yard care, tech
              setup, event help — we handle it, and we do it honestly.
            </p>
            <p>
              No upselling, no cutting corners, no mystery fees. Just practical fixes, friendly
              communication, and the kind of reliability that turns one-time customers into
              long-term neighbors.
            </p>
            <p className="text-slate-900 font-semibold">
              Affordability. Incredible customer service. Common sense fixes. That&apos;s the promise.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
